import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Clock, Play, Square, Mic, MicOff, LogOut, MessageSquare, RefreshCw, Coffee } from 'lucide-react';
import { User, WorkEntry, Project, Location } from '../types';
import { getProjects, getLocations } from '../utils/dataManager';
import { apiService } from '../services/api';
import { getCurrentTimestamp, getCurrentDateString, formatRomanianTime } from '../utils/timeUtils';
import TableActionNotification from './TabletActionNotification';
import { MaterialConsumptionList } from './materials/MaterialConsumptionList';
import { useRealTime } from '../contexts/RealTimeContext';
import { useAutomaticBreak, automaticBreakService } from '../services/automaticBreakService';
import { useServerTime } from '../services/timeService';

interface WorkerDashboardProps {
  user: User;
  onLogout: () => void;
}

const WorkerDashboard: React.FC<WorkerDashboardProps> = ({ user, onLogout }) => {
  const [currentEntry, setCurrentEntry] = useState<WorkEntry | null>(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [comment, setComment] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>('synced');
  
  // Add real-time context
  const realTime = useRealTime();
  
  // Server time synchronization
  const { currentTime, formatTime, isInitialized: timeServiceInitialized } = useServerTime();
  
  // Automatic break service
  const { status: automaticBreakStatus, forceCheck: forceBreakCheck } = useAutomaticBreak();


  const syncWorkEntryState = async (showLogs = false): Promise<WorkEntry | null> => {
    try {
      // Use the optimized status endpoint instead of full work entry
      const status = await apiService.getWorkerStatus(user.id);
      
      if (status.isWorking && status.workEntryId) {
        // Only fetch full entry if actively working
        const freshEntry = await apiService.getActiveWorkEntry(user.id, true);
        
        if (freshEntry) {
          if (showLogs) {
            console.log('Work entry state synced:', {
              entryId: freshEntry.id,
              status: status
            });
          }
          
          setCurrentEntry(freshEntry);
          return freshEntry;
        }
      } else {
        setCurrentEntry(null);
        return null;
      }
    } catch (error) {
      console.error('Failed to sync work entry state:', error);
      return null;
    }
  };

  useEffect(() => {
    return () => {
      setCurrentEntry(null);
    };
  }, []);

  useEffect(() => {
    loadData();
    checkActiveSession();
    
    // Sync state every 30 seconds
    const intervalId = setInterval(() => {
      if (currentEntry && !isLoading) {
        syncWorkEntryState().catch(console.error);
      }
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize SignalR connection
  useEffect(() => {
    const initRealTime = async () => {
      try {
        console.log('WorkerDashboard: Checking real-time connection state:', realTime.state.connectionState);
        if (realTime.state.connectionState === 'Disconnected') {
          console.log('WorkerDashboard: Attempting to connect to real-time service');
          await realTime.connect(user.id.toString(), user.role);
        }
      } catch (error) {
        console.error('WorkerDashboard: Error connecting to real-time service:', error);
      }
    };

    initRealTime();
  }, [user.id, user.role, realTime]);

  // Set up real-time event listeners
  useEffect(() => {
    if (!realTime.state.isConnected) return;

    console.log('WorkerDashboard: Setting up real-time event listeners');
    
    // Listen for optimized worker status changes
    const handleStatusChange = (status: { userId: number; isWorking: boolean; workEntryId?: number }) => {
      if (status.userId === user.id) {
        console.log('WorkerDashboard: Received status change for this worker:', status);
        
        if (status.isWorking && status.workEntryId) {
          // Fetch full entry details if working
          apiService.getActiveWorkEntry(user.id, true)
            .then(entry => {
              if (entry) setCurrentEntry(entry);
            })
            .catch(console.error);
        } else {
          // Not working anymore
          setCurrentEntry(null);
        }
      }
    };
    
    // Listen for traditional work events as fallback
    const handleWorkEvent = () => {
      console.log('WorkerDashboard: Received work status change, refreshing state');
      syncWorkEntryState(true).then(entry => {
        if (entry) {
          setCurrentEntry(entry);
        }
      }).catch(console.error);
    };

    // Subscribe to both optimized and traditional events
    const unsubscribeStatus = realTime.subscribeToCustomEvent('WorkerStatusChanged', handleStatusChange);
    realTime.subscribeToWorkEvents(handleWorkEvent);

    return () => {
      console.log('WorkerDashboard: Cleaning up real-time event listeners');
      unsubscribeStatus();
    };
  }, [realTime.state.isConnected, user.id, realTime, syncWorkEntryState]);

  const checkActiveSession = async (showNotifications = true) => {
    try {
      setSyncStatus('syncing');
      const activeEntry = await apiService.getActiveWorkEntry(user.id, true);
      
      if (activeEntry) {
        const entryDate = new Date(activeEntry.startTime).toDateString();
        const today = new Date().toDateString();
        
        if (entryDate === today) {
          console.log('Restoring active work session for', user.name, activeEntry);
          setCurrentEntry(activeEntry);
          setSyncStatus('synced');
          
          if (showNotifications) {
            // Check if work was started from table
            const tableActions = localStorage.getItem('table-actions');
            if (tableActions) {
              try {
                const actions = JSON.parse(tableActions);
                const recentTableStart = actions.find((a: { workerName: string; action: string; timestamp: string }) => 
                  a.workerName === user.name && 
                  a.action === 'start' &&
                  new Date(a.timestamp) > new Date(activeEntry.startTime)
                );
                
                if (recentTableStart) {
                  setTimeout(() => {
                    alert(`Lucrul tău a fost început de ${recentTableStart.tableName} la ${recentTableStart.projectName} - ${recentTableStart.locationName}`);
                  }, 500);
                }
              } catch (error) {
                console.error('Error checking table actions:', error);
              }
            }
            
            setTimeout(() => {
              try {
                alert('Sesiunea ta de lucru a fost restaurată. Poți continua să lucrezi.');
              } catch (notificationError) {
                console.error('Error showing notification:', notificationError);
              }
            }, 500);
          }
        } else {
          console.warn('Found stale work entry from previous day, ignoring');
          setCurrentEntry(null);
          setSyncStatus('synced');
        }
      } else {
        setCurrentEntry(null);
        setSyncStatus('synced');
      }
    } catch (error) {
      console.error('Error checking active session:', error);
      setSyncStatus('error');
      
      if (showNotifications) {
        alert('Nu am putut restaura sesiunea anterioară. Poți începe o nouă sesiune de lucru.');
      }
      
      setCurrentEntry(null);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [projectsData, locationsData] = await Promise.all([
        getProjects(),
        getLocations()
      ]);
      setProjects(projectsData);
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startWork = async () => {
    if (!selectedProject || !selectedLocation) {
      alert('Vă rugăm să selectați proiectul și locația');
      return;
    }

    setIsLoading(true);
    
    try {
      // Use supervisor method for workers to trigger SignalR events
      // This ensures other screens get updated when worker starts work
      const workEntry = await apiService.startWorkForUser(user.id, {
        userId: user.id,
        projectId: parseInt(selectedProject),
        locationId: parseInt(selectedLocation),
        startTime: getCurrentTimestamp(),
        date: getCurrentDateString()
      });
      
      console.log('WorkerDashboard: Started work with SignalR notification');
      setCurrentEntry(workEntry);
    } catch (error) {
      console.error('Error starting work:', error);
      alert('Eroare la începerea lucrului');
    } finally {
      setIsLoading(false);
    }
  };


  const endWork = async () => {
    if (!currentEntry) return;

    setIsLoading(true);
    try {
      // Use supervisor method for workers to trigger SignalR events
      // This ensures other screens get updated when worker ends work
      await apiService.endWorkForUser(currentEntry.id, {
        endTime: getCurrentTimestamp(),
        breakMinutes: 0, // Workers handle breaks separately
        comments: workDescription || undefined
      });
      
      console.log('WorkerDashboard: Ended work with SignalR notification');

      alert('Pontajul a fost înregistrat cu succes!');
      
      // Clear all state
      setCurrentEntry(null);
      setWorkDescription('');
      setComment('');
      
      // Clear cache
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
    } catch (error) {
      console.error('Error ending work:', error);
      alert('Eroare la salvarea pontajului');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      setTimeout(() => {
        setIsRecording(false);
        setWorkDescription(prev => prev + ' [Înregistrare vocală]');
      }, 3000);
    }
  };


  const addComment = async () => {
    if (!currentEntry || !comment.trim()) return;

    setIsLoading(true);
    try {
      await apiService.addComment(currentEntry.id, comment);
      setComment('');
      alert('Comentariul a fost adăugat cu succes!');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Eroare la adăugarea comentariului');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <TableActionNotification userId={user.id} userRole={user.role} />
      <div className="max-w-md mx-auto space-y-4">
        {/* Header */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3">
                  <CardTitle className="text-xl">Bună, {user.name}!</CardTitle>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${
                      realTime.state.isConnected ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <span className={`text-xs font-medium ${
                      realTime.state.isConnected ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {realTime.state.isConnected ? 'Live' : 'Offline'}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-2xl font-bold text-primary">{formatTime('time')}</p>
                  {!timeServiceInitialized && (
                    <p className="text-xs text-orange-600">Sincronizare server...</p>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={onLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Automatic Break Status */}
        {automaticBreakStatus && (
          <Card className={`border-2 ${automaticBreakStatus.isActive ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Coffee className={`w-5 h-5 ${automaticBreakStatus.isActive ? 'text-orange-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-sm">
                      {automaticBreakStatus.isActive ? 'Pauza automată activă' : 'Pauza automată inactivă'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {automaticBreakStatus.message}
                    </p>
                  </div>
                </div>
                {automaticBreakStatus.isActive && (
                  <div className="text-right">
                    <p className="text-xs font-medium text-orange-600">
                      12:00-13:00
                    </p>
                    <p className="text-xs text-gray-500">
                      Ora României
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {!currentEntry ? (
          /* Start Work Form */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5" />
                Începe Lucrul
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-lg font-medium">Proiect:</Label>
                <div className="grid gap-2 mt-2">
                  {projects.map(project => (
                    <Button
                      key={project.id}
                      variant={selectedProject === project.id.toString() ? "default" : "outline"}
                      className={`justify-start h-12 ${
                        selectedProject === project.id.toString() 
                          ? 'bg-primary hover:bg-primary/90' 
                          : 'hover:bg-primary/10'
                      }`}
                      onClick={() => setSelectedProject(project.id.toString())}
                    >
                      {project.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-lg font-medium">Locație:</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {locations.map(location => (
                    <Button
                      key={location.id}
                      variant={selectedLocation === location.id.toString() ? "default" : "outline"}
                      className={`h-12 ${
                        selectedLocation === location.id.toString() 
                          ? 'bg-primary hover:bg-primary/90' 
                          : 'hover:bg-primary/10'
                      }`}
                      onClick={() => setSelectedLocation(location.id.toString())}
                    >
                      {location.name}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                onClick={startWork}
                className="w-full h-14 text-lg bg-green-500 hover:bg-green-600"
                disabled={!selectedProject || !selectedLocation || isLoading}
              >
                <Play className="w-5 h-5 mr-2" />
                {isLoading ? 'Se pornește...' : 'Începe Lucrul'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Active Work Session */
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    Lucrezi Acum
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => checkActiveSession(true)}
                    disabled={isLoading || syncStatus === 'syncing'}
                    title="Sincronizează sesiunea"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''} ${syncStatus === 'error' ? 'text-red-500' : ''}`} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50">
                  <p className="font-medium">Proiect: {currentEntry.projectName}</p>
                  <p className="font-medium">Locație: {currentEntry.locationName}</p>
                  <p className="text-sm text-gray-600">
                    Început la: {formatRomanianTime(currentEntry.startTime)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Adaugă Comentariu
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="comment" className="text-lg font-medium">
                    Comentariu:
                  </Label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="mt-2"
                    placeholder="Scrie un comentariu despre lucrul tău..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={addComment}
                  className="w-full h-12 text-lg bg-blue-500 hover:bg-blue-600"
                  disabled={isLoading || !comment.trim()}
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  {isLoading ? 'Se adaugă...' : 'Adaugă Comentariu'}
                </Button>
              </CardContent>
            </Card>

            {/* Material Consumption Section */}
            <Card>
              <CardContent className="pt-6">
                <MaterialConsumptionList
                  workEntryId={currentEntry.id}
                  addedForUserId={user.id}
                  canEdit={true}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Square className="w-5 h-5" />
                  Finalizează Lucrul
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="workDescription" className="text-lg font-medium">
                    Descriere finală (opțional):
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="workDescription"
                      value={workDescription}
                      onChange={(e) => setWorkDescription(e.target.value)}
                      className="h-12 text-lg"
                      placeholder="Scrie aici sau înregistrează..."
                    />
                    <Button
                      onClick={toggleRecording}
                      variant={isRecording ? "destructive" : "outline"}
                      className="h-12 px-4"
                    >
                      {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </Button>
                  </div>
                  {isRecording && (
                    <p className="text-red-500 text-sm mt-1 animate-pulse">
                      🔴 Se înregistrează...
                    </p>
                  )}
                </div>

                <Button
                  onClick={endWork}
                  className="w-full h-14 text-lg bg-red-500 hover:bg-red-600"
                  disabled={isLoading}
                >
                  <Square className="w-5 h-5 mr-2" />
                  {isLoading ? 'Se finalizează...' : 'Termină Lucrul'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

    </div>
  );
};

export default WorkerDashboard;